/**
 * European Autospares — Vehicle Finder (Make > Model > Year)
 * Reads assets/vehicle-fitment-data.json and redirects the shopper to
 * matching parts once Make/Model/(Year) are chosen.
 *
 * Result URL styles (set in the "Result URL format" theme setting):
 *  - "search": Shopify product search for Make + Model + Year
 *              (default; works immediately, no admin setup required)
 *  - "filter": Shopify Search & Discovery metafield filters
 *              (filter.p.m.custom.fits_models / fits_years — requires
 *              those metafields to exist on products AND be enabled as
 *              filters in Shopify Admin > Search & Discovery)
 *  - "path":   a collection handle built from make + model
 *              (requires matching collections to exist, e.g.
 *              /collections/porsche-911)
 */
(function () {
  var dataPromise = null;

  function loadData() {
    if (!dataPromise) {
      var url = window.EAS_VEHICLE_DATA_URL;
      dataPromise = fetch(url)
        .then(function (res) { return res.json(); })
        .catch(function (err) {
          console.error('Vehicle Finder: failed to load fitment data', err);
          return {};
        });
    }
    return dataPromise;
  }

  function clearSelect(select, placeholder) {
    select.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    select.appendChild(ph);
  }

  function populateSelect(select, values, placeholder) {
    clearSelect(select, placeholder);
    values.forEach(function (v) {
      var o = document.createElement('option');
      o.value = v;
      o.textContent = v;
      select.appendChild(o);
    });
    select.disabled = values.length === 0;
  }

  function yearsForModel(variants) {
    var years = {};
    (variants || []).forEach(function (v) {
      if (v.year_from && v.year_to) {
        for (var y = v.year_from; y <= v.year_to; y++) years[y] = true;
      } else if (v.year_from) {
        years[v.year_from] = true;
      }
    });
    return Object.keys(years).map(Number).sort(function (a, b) { return b - a; }).map(String);
  }

  function slugify(str) {
    return (str || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function initFinder(root) {
    var makeSel = root.querySelector('[data-vf-make]');
    var modelSel = root.querySelector('[data-vf-model]');
    var yearSel = root.querySelector('[data-vf-year]');
    var submitBtn = root.querySelector('[data-vf-submit]');
    var toggleBtn = root.querySelector('[data-vf-toggle]');
    var panel = root.querySelector('[data-vf-panel]');

    // These match the attributes actually rendered by sections/vehicle-finder.liquid.
    // (Previously this read a "data-vf-redirect-base" attribute that section never
    // rendered, so custom Results Page / URL format settings were silently ignored.)
    var target = root.getAttribute('data-vf-target') || '/collections/all';
    var paramStyle = root.getAttribute('data-vf-param-style') || 'search';
    var yearRequired = root.getAttribute('data-vf-year-required') === 'true';

    if (!makeSel || !modelSel || !yearSel || !submitBtn) return;

    if (toggleBtn && panel) {
      toggleBtn.addEventListener('click', function () {
        var isHidden = panel.hasAttribute('hidden');
        if (isHidden) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }

    loadData().then(function (data) {
      populateSelect(makeSel, Object.keys(data).sort(), 'Select Make');
      makeSel.disabled = false;
      submitBtn.disabled = true;

      var preselect = root.getAttribute('data-vf-preselect');
      if (preselect && data[preselect]) {
        makeSel.value = preselect;
        makeSel.disabled = true;
        populateSelect(modelSel, Object.keys(data[preselect]).sort(), 'Select Model');
      }

      makeSel.addEventListener('change', function () {
        var make = makeSel.value;
        clearSelect(modelSel, 'Select Model');
        clearSelect(yearSel, 'Select Year (optional)');
        modelSel.disabled = true;
        yearSel.disabled = true;
        submitBtn.disabled = true;
        if (!make || !data[make]) return;
        populateSelect(modelSel, Object.keys(data[make]).sort(), 'Select Model');
      });

      modelSel.addEventListener('change', function () {
        var make = makeSel.value;
        var model = modelSel.value;
        clearSelect(yearSel, 'Select Year (optional)');
        yearSel.disabled = true;
        submitBtn.disabled = !model || yearRequired;
        if (!make || !model || !data[make] || !data[make][model]) return;
        var years = yearsForModel(data[make][model]);
        populateSelect(yearSel, years, 'Select Year (optional)');
      });

      yearSel.addEventListener('change', function () {
        if (yearRequired) submitBtn.disabled = !modelSel.value || !yearSel.value;
      });
    });

    submitBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var make = makeSel.value;
      var model = modelSel.value;
      var year = yearSel.value;

      if (!model) {
        modelSel.focus();
        modelSel.reportValidity && modelSel.reportValidity();
        return;
      }
      if (yearRequired && !year) {
        yearSel.focus();
        yearSel.reportValidity && yearSel.reportValidity();
        return;
      }

      var url;

      if (paramStyle === 'filter') {
        // Requires custom.fits_models / custom.fits_years metafields to exist on
        // products AND be enabled as filters in Shopify Admin > Search & Discovery.
        var filterParams = new URLSearchParams();
        filterParams.set('filter.p.m.custom.fits_models', model);
        if (year) filterParams.set('filter.p.m.custom.fits_years', year);
        url = target + '?' + filterParams.toString();
      } else if (paramStyle === 'path') {
        // Requires a matching collection to exist, e.g. /collections/porsche-911
        var handle = slugify(make) + '-' + slugify(model);
        url = '/collections/' + handle;
      } else {
        // "search" (default): native Shopify product search — works immediately,
        // no metafields or extra collections required.
        var q = [make, model, year].filter(Boolean).join(' ');
        var searchParams = new URLSearchParams();
        searchParams.set('q', q);
        searchParams.set('type', 'product');
        var searchBase = (target && target !== '/collections/all') ? target : '/search';
        url = searchBase + '?' + searchParams.toString();
      }

      window.location.href = url;
    });
  }

  function init() {
    document.querySelectorAll('[data-vehicle-finder]').forEach(initFinder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();